import { useEffect, useState, type ReactNode } from 'react'
import { ProConfigProvider } from '@ant-design/pro-components'
import { theme as antdTheme, App, ConfigProvider } from 'antd'
import zhCN from 'antd/locale/zh_CN'
import { FORM_ERROR_SCROLL_OPTIONS } from '@/const/form.ts'
import { useThemeStore, type ThemeMode } from '@/stores/theme-store.ts'

type ResolvedTheme = Exclude<ThemeMode, 'system'>

function applyDocumentTheme(mode: ThemeMode, resolvedTheme: ResolvedTheme) {
  const root = document.documentElement
  root.dataset.themeMode = mode
  root.dataset.theme = resolvedTheme
  root.classList.toggle('dark', resolvedTheme === 'dark')
  root.style.colorScheme = resolvedTheme
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const mode = useThemeStore((state) => state.mode)
  const primaryColor = useThemeStore((state) => state.primaryColor)
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  )
  const resolvedTheme = mode === 'system' ? systemTheme : mode
  const isDark = resolvedTheme === 'dark'

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  useEffect(() => {
    applyDocumentTheme(mode, resolvedTheme)
  }, [mode, resolvedTheme])

  useEffect(() => {
    // 使用 Ant Design 官方算法同步页面语义色，暗色模式也跟随同一套色阶。
    const token = antdTheme.getDesignToken({
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: { colorPrimary: primaryColor }
    })
    const root = document.documentElement
    root.style.setProperty('--primary', token.colorPrimary)
    root.style.setProperty('--primary-600', token.colorPrimaryHover)
    root.style.setProperty('--accent-soft', token.colorPrimaryBg)
  }, [isDark, primaryColor])

  return (
    <ConfigProvider
      locale={zhCN}
      form={{ scrollToFirstError: FORM_ERROR_SCROLL_OPTIONS }}
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: primaryColor
        }
      }}
      modal={{
        centered: true,
        styles: {
          body: {
            flex: '1 1 auto',
            minHeight: 0,
            overflowX: 'hidden',
            overflowY: 'auto'
          },
          container: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '80vh',
            overflow: 'hidden'
          },
          footer: { flex: '0 0 auto' },
          header: { flex: '0 0 auto' }
        }
      }}
    >
      <ProConfigProvider dark={isDark}>
        <App>{children}</App>
      </ProConfigProvider>
    </ConfigProvider>
  )
}
